import React from "react";
import LockedControl from "@/components/common/LockedControl";
import { useTrialAccount, type TrialLockKey } from "@/hooks/useTrialAccount";

interface TrialLockedProps {
  children: React.ReactNode;
  /** Which canned message to show; ignored when `message` is given. */
  lock?: TrialLockKey;
  /** Custom hover copy. */
  message?: string;
  /**
   * Override the lock state. Defaults to the current user's trial flag. Pass
   * `false` to render children untouched even on a trial account.
   */
  locked?: boolean;
  /** Render inline (`span`) instead of block. */
  inline?: boolean;
  className?: string;
  /** Names the focusable lock wrapper for screen readers (e.g. an icon-only control). */
  "aria-label"?: string;
  side?: React.ComponentProps<typeof LockedControl>["side"];
}

/**
 * Disables every form control inside it for trial accounts and explains why on
 * hover. Renders children untouched for full accounts, so call sites can wrap
 * unconditionally.
 */
const TrialLocked: React.FC<TrialLockedProps> = ({
  children,
  lock = "generic",
  message,
  locked,
  ...rest
}) => {
  const { isTrial, lockMessage } = useTrialAccount();

  return (
    <LockedControl
      locked={locked ?? isTrial}
      message={message ?? lockMessage(lock)}
      data-trial-locked=""
      {...rest}
    >
      {children}
    </LockedControl>
  );
};

export default TrialLocked;
